import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Briefcase, ChevronDown, CircleHelp, Settings } from 'lucide-react';
import {
  ACCOUNTANT_TOOLS,
  BUSINESS_GEAR_MENU,
  COMING_SOON,
  GEAR_MENU,
  isAvailable,
} from '@/app/navigation';
import { GlobalSearch } from '@/components/global-search';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { BusinessViewToggle, NotificationsBell } from '@/components/portal/notifications-bell';
import { ROLE_LABELS, formatTrn, useClients, useMe } from '@/lib/clients';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { auth } from '@/lib/auth';
import { stateName } from '@/lib/gst';
import { setOrgId, useOrgId } from '@/lib/org';
import { useBusinessView } from '@/lib/portal';
import { LanguageSwitch } from '@/components/language-switch';
import { navLabel, useLanguage, useT } from '@/lib/i18n';
import { useOpenClient } from '@/lib/use-open-client';

/** QBO-style client switcher: the orgs the signed-in user may open (GET /practice/clients). */
function ClientSwitcher() {
  const orgId = useOrgId();
  const clients = useClients(auth.enabled);
  const openClient = useOpenClient();
  const [filter, setFilter] = useState('');
  const current = clients.data?.find((c) => c.id === orgId);
  const business = useBusinessView();
  const t = useT();

  // Keep the stored choice valid: default to the first client, drop one the user lost access to.
  useEffect(() => {
    if (!clients.isSuccess) return;
    if (!orgId || !clients.data.some((c) => c.id === orgId)) setOrgId(clients.data[0]?.id ?? null);
  }, [clients.isSuccess, clients.data, orgId]);

  const shown = (clients.data ?? []).filter((c) =>
    c.name.toLowerCase().includes(filter.trim().toLowerCase()),
  );

  return (
    <DropdownMenu onOpenChange={(open) => !open && setFilter('')}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="min-w-0 flex-1 justify-between gap-2 font-medium lg:flex-none"
          data-testid="client-switcher"
        >
          <span className="hidden text-muted-foreground sm:inline">{t('shell.client')}</span>
          <span className="min-w-0 max-w-[260px] truncate">
            {current?.name ??
              (clients.isPending && auth.enabled ? t('common.loading') : t('shell.selectClient'))}
          </span>
          <ChevronDown className="size-4 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[min(360px,calc(100vw-1.5rem))]">
        <DropdownMenuLabel>{t('shell.switchClient')}</DropdownMenuLabel>
        {(clients.data?.length ?? 0) > 5 ? (
          <div className="px-2 pb-2">
            <Input
              autoFocus
              placeholder={t('shell.findClient')}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>
        ) : null}
        {clients.isError ? (
          <p className="px-2 py-1 text-xs text-destructive">{clients.error.message}</p>
        ) : null}
        {!auth.enabled ? (
          <p className="px-2 py-1 text-xs text-muted-foreground">{t('shell.signInDisabled')}</p>
        ) : null}
        {clients.isSuccess && clients.data.length === 0 ? (
          <p className="px-2 py-1 text-xs text-muted-foreground">{t('shell.noClients')}</p>
        ) : null}
        {shown.map((c) => (
          <DropdownMenuItem
            key={c.id}
            onSelect={() => openClient(c.id)}
            className={c.id === orgId ? 'bg-accent' : ''}
          >
            <div className="flex flex-col">
              <span>{c.name}</span>
              <span className="text-xs text-muted-foreground">
                {[
                  c.taxPack === 'in' ? (c.stateCode ? stateName(c.stateCode) : null) : c.emirate,
                  c.taxPack === 'in'
                    ? c.gstin
                      ? `GSTIN ${c.gstin}`
                      : null
                    : c.trn
                      ? `TRN ${formatTrn(c.trn)}`
                      : null,
                  t(`roles.${c.role}` as 'roles.client_owner', {
                    defaultValue: ROLE_LABELS[c.role] ?? c.role,
                  }),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </div>
          </DropdownMenuItem>
        ))}
        {business.on && !business.preview ? null : (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/practice/clients">{t('shell.viewAllClients')}</Link>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Accountant tools (briefcase): QBO Accountant's tool list, Outss staff only (document 05). */
function AccountantTools() {
  const me = useMe();
  const t = useT();
  const lang = useLanguage();
  if (!me.data?.isStaff) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('shell.accountantTools')}
          title={t('shell.accountantTools')}
        >
          <Briefcase className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>{t('shell.accountantTools')}</DropdownMenuLabel>
        {ACCOUNTANT_TOOLS.map((item) =>
          isAvailable(item.to) ? (
            <DropdownMenuItem key={item.label} asChild>
              <Link
                to={item.to.split('?')[0]!}
                search={Object.fromEntries(new URLSearchParams(item.to.split('?')[1] ?? ''))}
              >
                {navLabel(item.label, lang)}
              </Link>
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              key={item.label}
              disabled
              title={navLabel(COMING_SOON, lang)}
              data-unavailable
            >
              {navLabel(item.label, lang)}
            </DropdownMenuItem>
          ),
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Top bar: client switcher · search · notifications · help · accountant tools · gear (document 05).
 * Below `lg` the search box drops to a second, full-width row so the icons stay on screen.
 */
export function TopBar() {
  const business = useBusinessView();
  // client users get their own short gear menu; staff previewing the Business view keep theirs
  const gear = business.on && !business.preview ? BUSINESS_GEAR_MENU : GEAR_MENU;
  const t = useT();
  const lang = useLanguage();
  const L = (label: string) => navLabel(label, lang);
  return (
    <header className="flex min-h-14 flex-wrap items-center gap-x-1 gap-y-2 border-b bg-card px-3 py-2 sm:gap-x-3 lg:h-14 lg:flex-nowrap lg:py-0">
      <SidebarTrigger />

      <ClientSwitcher />

      <GlobalSearch />

      <BusinessViewToggle />
      <LanguageSwitch signedIn className="px-2" />
      <NotificationsBell />
      <Button
        variant="ghost"
        size="icon"
        aria-label={t('shell.help')}
        title={t('shell.keyboardShortcuts')}
        className="hidden md:inline-flex"
        asChild
      >
        <Link to="/help/shortcuts">
          <CircleHelp className="size-4" />
        </Link>
      </Button>
      <AccountantTools />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" aria-label={t('shell.settings')}>
            <Settings className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className={
            gear === GEAR_MENU
              ? 'max-h-[80vh] w-[min(720px,calc(100vw-1.5rem))] overflow-y-auto p-4'
              : 'max-h-[80vh] w-[min(400px,calc(100vw-1.5rem))] overflow-y-auto p-4'
          }
        >
          <div
            className={
              gear === GEAR_MENU
                ? 'grid grid-cols-2 gap-6 md:grid-cols-4'
                : 'grid grid-cols-2 gap-6'
            }
          >
            {gear.map((col) => (
              <div key={col.heading}>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {L(col.heading)}
                </div>
                <ul className="space-y-1">
                  {col.items.map((item) => (
                    <li key={item.to} title={isAvailable(item.to) ? undefined : L(COMING_SOON)}>
                      {isAvailable(item.to) ? (
                        <DropdownMenuItem asChild className="px-1 py-1">
                          <Link to={item.to}>{L(item.label)}</Link>
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem disabled className="px-1 py-1" data-unavailable>
                          {L(item.label)}
                        </DropdownMenuItem>
                      )}
                    </li>
                  ))}
                  {col.heading === 'Profile' && auth.enabled ? (
                    <li>
                      <DropdownMenuItem className="px-1 py-1" onSelect={() => void auth.signOut()}>
                        {t('shell.signOut')}
                      </DropdownMenuItem>
                    </li>
                  ) : null}
                </ul>
              </div>
            ))}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
